/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { type AxiosInstance } from 'axios';
import { createQuery } from '@/utils/createReactQuery';
import { useLocale } from '@/hooks/useLocale';
import type { Locale } from '@/types/i18n';
import type {} from '@/features/contentstack/interface';
// eslint-disable-next-line no-restricted-imports
import { getExperienceInjectorContentV2 } from '@/features/contentstack/lib/common/getExperienceInjectorContentV2';
import { type FormattedContentStackExperienceInjectorV2 } from '@/features/contentstack/interface/models';
import { type QueryClient } from '@tanstack/react-query';
import { type PageContextType } from '@/next-types';
const CMS_URL = '/api/contentstack/experience-injector';

type Options = {
  locale: Locale;
};

const getCMSExperienceInjectorContent = async (axiosInstance?: AxiosInstance) => {
  const { data } =
    await getAxios(axiosInstance).get<FormattedContentStackExperienceInjectorV2>(CMS_URL);
  return data;
};

const { useData: useExperienceInjector, query: experienceInjectorQuery } = createQuery<
  FormattedContentStackExperienceInjectorV2 | null,
  Options
>('experienceInjectorData', async (options, axiosInstance) =>
  getCMSExperienceInjectorContent(axiosInstance),
);

const useExperienceInjectorData = () => {
  const locale = useLocale();

  return useExperienceInjector({
    locale,
    staleTime: Infinity,
  });
};

const prefetchCMSExperienceInjectorContent = (
  queryClient: QueryClient,
  context: PageContextType,
  options: Options,
) => {
  return queryClient.prefetchQuery({
    queryKey: experienceInjectorQuery.getFullKey(options),
    queryFn: () => getExperienceInjectorContentV2(context.req),
  });
};

export { prefetchCMSExperienceInjectorContent, useExperienceInjectorData };
